import React, { useState, useEffect, useRef, useCallback } from "react";
import { graphql, useStaticQuery } from "gatsby";
import { GatsbyImage, getImage } from "gatsby-plugin-image";

// Gallery + Lightbox that loads images from Gatsby File nodes
const Gallery = () => {
		const data = useStaticQuery(graphql`
			query GalleryImagesWithMeta {
				allFile(
					filter: { extension: { regex: "/(jpg|jpeg|png|gif|webp)/" }, relativeDirectory: { regex: "/illustrations/" } }
				) {
					nodes {
						id
						name
						relativeDirectory
						publicURL
						childImageSharp {
							gatsbyImageData(width: 600, placeholder: BLURRED, formats: [AUTO, WEBP])
						}
					}
				}
				allMarkdownRemark(
					filter: { fileAbsolutePath: { regex: "/content/illustrations/" } }
					sort: { fields: [frontmatter___title], order: [ASC] }
				) {
					nodes {
						id
						fileAbsolutePath
						frontmatter {
							title
							image
							alt
							description
							order
						}
					}
				}
				galleryOrder: allMarkdownRemark(
					filter: { fileAbsolutePath: { regex: "/content/settings/gallery-order.md/" } }
				) {
					nodes {
						frontmatter { orderImages }
					}
				}
			}
		`);

		const files = data?.allFile?.nodes || [];
			const allMd = data?.allMarkdownRemark?.nodes || [];
			// already filtered and sorted from the GraphQL query (alphabetical by title)
			let entries = allMd;

			// apply optional drag-and-drop order from settings if present
			const settingsOrderImages = data?.galleryOrder?.nodes?.[0]?.frontmatter?.orderImages || [];
			if (Array.isArray(settingsOrderImages) && settingsOrderImages.length > 0) {
				const indexByBase = new Map(
					settingsOrderImages.map((imgPath, i) => [String(imgPath).split("/").pop(), i])
				);
				entries = [...entries].sort((a, b) => {
					const aImg = (a.frontmatter?.image || "").split("/").pop();
					const bImg = (b.frontmatter?.image || "").split("/").pop();
					const ai = indexByBase.has(aImg) ? indexByBase.get(aImg) : Number.POSITIVE_INFINITY;
					const bi = indexByBase.has(bImg) ? indexByBase.get(bImg) : Number.POSITIVE_INFINITY;
					if (ai !== bi) return ai - bi;
					return 0;
				});
			}

		// build lookup by filename for fast matching (case-insensitive)
		const fileByName = {};
		files.forEach((f) => {
			const parts = (f.publicURL || "").split("/");
			const basename = parts[parts.length - 1];
			fileByName[basename] = f;
			fileByName[basename.toLowerCase()] = f; // map lowercase for case-insensitive matching
			fileByName[f.name] = f; // also map by name without extension
			fileByName[f.name.toLowerCase()] = f; // map lowercase name too
		});

		// map markdown entries to images, preserving order of entries
		const images = entries
			.map((md) => {
				const imgPath = md.frontmatter?.image || "";
				const imgName = imgPath.split("/").pop();
				// try exact match first, then case-insensitive match
				const file = fileByName[imgName] || fileByName[imgName.toLowerCase()] || fileByName[imgName.replace(/\?.*$/, "")];
				return {
					id: md.id,
					title: md.frontmatter?.title || imgName || "",
					alt: md.frontmatter?.alt || md.frontmatter?.title || "",
					desc: md.frontmatter?.description || "",
					thumb: file?.childImageSharp ? getImage(file.childImageSharp.gatsbyImageData) : null,
					full: file?.publicURL || imgPath || null,
				};
			})
			.filter(Boolean);

	const [open, setOpen] = useState(false);
	const [currentIndex, setCurrentIndex] = useState(0);
	const lightboxRef = useRef(null);
	const closeBtnRef = useRef(null);
	const startXRef = useRef(0);

	const openAt = useCallback((index) => {
		setCurrentIndex(index);
		setOpen(true);
	}, []);

	const close = useCallback(() => setOpen(false), []);
	const prev = useCallback(() => setCurrentIndex((i) => Math.max(0, i - 1)), []);
	const next = useCallback(() => setCurrentIndex((i) => Math.min(images.length - 1, i + 1)), [images.length]);

	useEffect(() => {
		function onKey(e) {
			if (!open) return;
			if (e.key === "Escape") close();
			if (e.key === "ArrowLeft") prev();
			if (e.key === "ArrowRight") next();
		}
		document.addEventListener("keydown", onKey);
		return () => document.removeEventListener("keydown", onKey);
	}, [open, close, prev, next]);

	useEffect(() => {
		if (open && closeBtnRef.current) closeBtnRef.current.focus();
	}, [open]);

	useEffect(() => {
		const lb = lightboxRef.current;
		if (!lb) return;
		function onTouchStart(e) {
			startXRef.current = e.touches[0].clientX;
		}
		function onTouchEnd(e) {
			const diff = e.changedTouches[0].clientX - startXRef.current;
			if (diff > 50) prev();
			else if (diff < -50) next();
		}
		lb.addEventListener("touchstart", onTouchStart, { passive: true });
		lb.addEventListener("touchend", onTouchEnd, { passive: true });
		return () => {
			lb.removeEventListener("touchstart", onTouchStart);
			lb.removeEventListener("touchend", onTouchEnd);
		};
	}, [prev, next]);

	const gridRef = useRef(null);
	const itemRefs = useRef([]);

	// masonry layout effect
	useEffect(() => {
		if (!gridRef.current || images.length === 0) return;

		const layoutMasonry = () => {
			const grid = gridRef.current;
			const items = itemRefs.current.filter(Boolean);
			if (items.length === 0) return;

			// determine number of columns based on viewport width
			const gridWidth = grid.offsetWidth;
			const minColWidth = 300;
			const gap = 16; // 16px = 1rem
			const numCols = Math.max(1, Math.min(4, Math.floor((gridWidth + gap) / (minColWidth + gap))));

			// initialize column heights
			const colHeights = Array(numCols).fill(0);
			const colWidth = (gridWidth - gap * (numCols - 1)) / numCols;

			// masonry algorithm -- place each item in shortest column
			items.forEach((item, idx) => {
				if (!item) return;

				// find shortest column
				const shortestCol = colHeights.indexOf(Math.min(...colHeights));

				// Position item
				const left = shortestCol * (colWidth + gap);
				const top = colHeights[shortestCol];

				item.style.position = 'absolute';
				item.style.left = `${left}px`;
				item.style.top = `${top}px`;
				item.style.width = `${colWidth}px`;

				// Update column height (use actual rendered height)
				const itemHeight = item.offsetHeight;
				colHeights[shortestCol] += itemHeight + gap;
			});

			// Set container height to tallest column
			const maxHeight = Math.max(...colHeights);
			grid.style.height = `${maxHeight}px`;
		};

		// Initial layout
		layoutMasonry();

		// Re-layout on window resize
		const handleResize = () => layoutMasonry();
		window.addEventListener('resize', handleResize);

		// Re-layout when images load (for cases where GatsbyImage loads async)
		const timer = setTimeout(layoutMasonry, 100);

		return () => {
			window.removeEventListener('resize', handleResize);
			clearTimeout(timer);
		};
	}, [images]);

	if (!images || images.length === 0) return null;

	return (
		<>
			<div id="gallery" className="gallery">
				<div className="grid masonry" ref={gridRef}>
					{images.map((img, i) => (
						<div
							key={img.id}
							ref={(el) => (itemRefs.current[i] = el)}
							className="item"
							role="button"
							tabIndex={0}
							data-title={img.title}
							onClick={() => openAt(i)}
							onKeyDown={(e) => {
								if (e.key === "Enter" || e.key === " ") openAt(i);
							}}
						>
							{img.thumb ? (
								<GatsbyImage image={img.thumb} alt={img.title || `Image ${i + 1}`} />
							) : (
								<img src={img.full} alt={img.title || `Image ${i + 1}`} />
							)}
							<div className="overlay">{img.title}</div>
						</div>
					))}
				</div>
			</div>

			<div
				id="lightbox"
				ref={lightboxRef}
				className={`lightbox ${open ? "active" : ""}`}
				role="dialog"
				aria-modal="true"
				aria-label={images[currentIndex]?.title || "Image preview"}
				onClick={(e) => {
					if (e.target === lightboxRef.current) close();
				}}
			>
				<div className="lightbox-content">
					{images[currentIndex]?.full ? (
						<img id="lightbox-img" src={images[currentIndex].full} alt={images[currentIndex].title || `Image ${currentIndex + 1}`} />
					) : (
						images[currentIndex]?.thumb && <GatsbyImage image={images[currentIndex].thumb} alt={images[currentIndex].title} />
					)}
					<div className="lightbox-info">
						<h3 id="lightbox-title">{images[currentIndex].title}</h3>
					</div>
				</div>

				<button
					id="prev"
					className="lightbox-nav prev"
					onClick={prev}
					style={{ display: currentIndex === 0 ? "none" : "flex" }}
					aria-label="Previous image"
				>
					‹
				</button>

				<button
					id="next"
					className="lightbox-nav next"
					onClick={next}
					style={{ display: currentIndex === images.length - 1 ? "none" : "flex" }}
					aria-label="Next image"
				>
					›
				</button>

				<button id="close" ref={closeBtnRef} className="lightbox-close" onClick={close} aria-label="Close lightbox">
					×
				</button>
			</div>
		</>
	);
};

export default Gallery;